import type React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
	Home,
	Target,
	Calendar,
	Book,
	MessageSquare,
	Users,
	Clock,
	LayoutDashboard,
	UserCog,
	CalendarClock,
	FileText,
	Hash,
	ShieldAlert,
	LayoutGrid,
} from "lucide-react";
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
} from "../../ui/sidebar";
import { useAuth } from "@/hooks/useAuth";

import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { ChannelList } from "./ChannelList";

const mainMenuItems = [
	{
		title: "ダッシュボード",
		icon: Home,
		url: "/webapp/dashboard",
	},
	{
		title: "目標",
		icon: Target,
		url: "/webapp/goals",
	},

	{
		title: "日記",
		icon: Book,
		url: "/webapp/diary",
	},
	{
		title: "イベント",
		icon: Users,
		url: "/webapp/events",
	},
	{
		title: "カレンダー",
		icon: Calendar,
		url: "/webapp/calendar",
	},
	{
		title: "ポモドーロタイマー",
		icon: Clock,
		url: "/webapp/pomodoro",
	},
];

const adminMenuItems = [
	{
		title: "管理ダッシュボード",
		icon: LayoutDashboard,
		url: "/webapp/admin/dashboard",
	},
	{
		title: "ユーザー管理",
		icon: UserCog,
		url: "/webapp/admin/users",
	},
	{
		title: "イベント管理",
		icon: CalendarClock,
		url: "/webapp/admin/events",
	},
	{
		title: "コンテンツ管理",
		icon: FileText,
		url: "/webapp/admin/contents",
	},
	{
		title: "お問い合わせ管理",
		icon: MessageSquare,
		url: "/webapp/admin/contacts",
	},
	{
		title: "チャンネル管理",
		icon: Hash,
		url: "/webapp/admin/channels",
	},
];

const MenuItems: React.FC<{
	items: typeof mainMenuItems;
	showChannels?: boolean;
}> = ({ items, showChannels }) => (
	<div className="space-y-6">
		<SidebarMenu>
			{items.map((item) => (
				<SidebarMenuItem key={item.title}>
					<SidebarMenuButton asChild tooltip={item.title}>
						<Link to={item.url}>
							<item.icon className="mr-2 h-4 w-4" />
							<span>{item.title}</span>
						</Link>
					</SidebarMenuButton>
				</SidebarMenuItem>
			))}
		</SidebarMenu>

		{/* チャンネル一覧を表示（サイドバーが閉じているときは非表示） */}
		{showChannels && (
			<div className="space-y-1 group-data-[collapsible=icon]:hidden">
				<SidebarGroupLabel>チャンネル</SidebarGroupLabel>
				<ChannelList />
			</div>
		)}
	</div>
);

const menuVariants = {
	enter: (direction: number) => ({
		x: direction > 0 ? 100 : -100,
		opacity: 0,
	}),
	center: {
		x: 0,
		opacity: 1,
	},
	exit: (direction: number) => ({
		x: direction < 0 ? 100 : -100,
		opacity: 0,
	}),
};

const SidebarNavigation: React.FC = () => {
	const { user } = useAuth();
	const [activeMenu, setActiveMenu] = useState<"main" | "admin">("main");
	const [[page, direction], setPage] = useState([0, 0]);

	const paginate = (newDirection: number) => {
		const newMenu = activeMenu === "main" ? "admin" : "main";
		setPage([page + newDirection, newDirection]);
		setActiveMenu(newMenu);
	};

	if (user?.role !== "admin") {
		return (
			<SidebarGroup>
				<SidebarGroupLabel>メインメニュー</SidebarGroupLabel>
				<MenuItems items={mainMenuItems} showChannels={true} />
			</SidebarGroup>
		);
	}

	return (
		<div className="flex flex-col h-full">
			<div className="flex-grow relative overflow-hidden">
				<AnimatePresence initial={false} custom={direction} mode="wait">
					<motion.div
						key={activeMenu}
						custom={direction}
						variants={menuVariants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{
							type: "spring",
							stiffness: 300,
							damping: 30,
						}}
						className="absolute w-full"
					>
						{activeMenu === "main" ? (
							<SidebarGroup>
								<SidebarGroupLabel>メインメニュー</SidebarGroupLabel>
								<MenuItems items={mainMenuItems} showChannels={true} />
							</SidebarGroup>
						) : (
							<SidebarGroup>
								<SidebarGroupLabel>管理者メニュー</SidebarGroupLabel>
								<MenuItems items={adminMenuItems} />
							</SidebarGroup>
						)}
					</motion.div>
				</AnimatePresence>
			</div>

			<motion.div
				className="mt-auto pt-4 border-t"
				initial={false}
				animate={{
					backgroundColor:
						activeMenu === "main" ? "var(--background)" : "var(--muted)",
				}}
				transition={{ duration: 0.3 }}
			>
				<Button
					variant="ghost"
					className="w-full justify-start text-muted-foreground hover:text-foreground group"
					onClick={() => paginate(activeMenu === "main" ? 1 : -1)}
				>
					<AnimatePresence mode="wait">
						<motion.div
							key={activeMenu}
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							transition={{ duration: 0.2 }}
							className="mr-2"
						>
							{activeMenu === "main" ? (
								<ShieldAlert className="h-4 w-4" />
							) : (
								<LayoutGrid className="h-4 w-4" />
							)}
						</motion.div>
					</AnimatePresence>
					<span>
						{activeMenu === "main"
							? "管理者メニューを表示"
							: "メインメニューを表示"}
					</span>
				</Button>
			</motion.div>
		</div>
	);
};

export default SidebarNavigation;
